import { HYPERBEAT_VAULT_ABI } from '@/config/abis/vaults/hyperbeatVaultAbi'
import { SWAP_VAULT_ABI } from '@/config/abis/vaults/swapVaultAbi'
import { TELLER_VAULT_ABI } from '@/config/abis/vaults/tellerVaultAbi'
import { TELLER_BOUNCER_VAULT_ABI } from '@/config/abis/vaults/tellterBouncerVaultAbi'
import { DEFAULT_SLIPPAGE, NATIVE_ASSET_MOCK_ADDRESS } from '@/config/consts'
import {
  createNativeCollateralDepositBatchConfig,
  createNativeDepositBatchConfig,
} from '@/domain/evc/transactionFactory'
import { ensureConfigTypes } from '@/domain/hooks/useWrite'
import { PERMIT2_ADDRESS } from '@/features/actions/flavours/permit2-sign/types'
import {
  getEulerPortfolioInvalidationKeys,
  getPortfolioBalanceInvalidationKeys,
} from '@/features/actions/logic/invalidation'
import { VaultType } from '@/features/usdxl/types/vaults'
import { toBigInt } from '@/utils/bigNumber'
import { calculateMinimumMint } from '@/utils/calculateMinimumMint'
import { queryOptions } from '@tanstack/react-query'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { permit2Abi } from 'abis/evm/permit2'
import { encodeFunctionData, zeroAddress } from 'viem'
import { simulateContract } from 'wagmi/actions'
import { ActionConfig, ActionContext, InitialParamsQueryResult } from '../../../logic/types'
import { VaultDepositAction } from '../types'

interface BatchItem {
  targetContract: `0x${string}`
  onBehalfOfAccount: `0x${string}`
  value: bigint
  data: `0x${string}`
}

export function createVaultDepositActionConfig(action: VaultDepositAction, context: ActionContext): ActionConfig {
  if (action.vaultType === VaultType.NUCLEUS) {
    return vaultNucleusDepositActionConfig(action, context)
  }

  if (action.vaultType === VaultType.EEARN) {
    return vaultEEarnDepositActionConfig(action, context)
  }

  return vaultHyperbeatDepositActionConfig(action, context)
}

function vaultHyperbeatDepositActionConfig(action: VaultDepositAction, context: ActionContext): ActionConfig {
  const { account, chainId } = context

  const depositAmount = toBigInt(action.token.toBaseUnit(action.value))
  const receiver = action.receiver ?? account

  return {
    getWriteConfig: () => {
      const configData = {
        abi: HYPERBEAT_VAULT_ABI,
        address: action.vaultAddress,
        functionName: 'deposit',
        chainId,
        args: [depositAmount, receiver],
      } as const

      return ensureConfigTypes(configData)
    },

    invalidates: () => getEulerPortfolioInvalidationKeys({ account, chainId }),
  }
}

function vaultEEarnDepositActionConfig(action: VaultDepositAction, context: ActionContext): ActionConfig {
  const { account, chainId, permit2Store } = context

  const depositAmount = toBigInt(action.token.toBaseUnit(action.value))
  const isNativeHYPE = action.token.address.toLowerCase() === NATIVE_ASSET_MOCK_ADDRESS.toLowerCase()
  const receiver = action.receiver ?? account

  return {
    getWriteConfig: () => {
      // If depositing native HYPE, use EVC batch via wagmi-evc's WETHTransactionFactory
      // This handles: wrap native → transfer to vault → skim into shares
      // If depositing native HYPE, batch wrap + approve + deposit via gateway
      if (isNativeHYPE) {
        const createNativeDepositConfig = action.enableAsCollateral
          ? createNativeCollateralDepositBatchConfig
          : createNativeDepositBatchConfig
        const configData = createNativeDepositConfig({
          vault: action.vaultAddress,
          amount: depositAmount,
          account,
          receiver,
          chainId,
        })

        return ensureConfigTypes(configData)
      }

      // Check if we have a Permit2 signature and EVC address
      const permit2Data = permit2Store?.find(action.token, action.vaultAddress)

      // If we have Permit2 signature and EVC address, use EVC batch with Permit2 permit call
      if (permit2Data && action.evcAddress) {
        const batchItems: BatchItem[] = []

        // 1. Include Permit2 permit call
        const permitSingle = {
          details: {
            token: action.token.address,
            amount: permit2Data.amount,
            expiration: permit2Data.expiration,
            nonce: permit2Data.nonce,
          },
          spender: permit2Data.spender,
          sigDeadline: permit2Data.sigDeadline,
        }

        const permitData = encodeFunctionData({
          abi: permit2Abi,
          functionName: 'permit',
          args: [account, permitSingle, permit2Data.signature],
        })

        batchItems.push({
          targetContract: PERMIT2_ADDRESS,
          onBehalfOfAccount: account, // User address for external contracts
          value: 0n,
          data: permitData,
        })

        if (action.enableAsCollateral) {
          const enableCollateralData = encodeFunctionData({
            abi: eulerVaultConnectorAbi,
            functionName: 'enableCollateral',
            args: [receiver, action.vaultAddress],
          })

          // EVC self-calls must use zeroAddress for onBehalfOfAccount or batch reverts with EVC_InvalidAddress.
          // Matches EVCTransactionFactory.convertEnableCollateral in @eulerxyz/wagmi-evc.
          batchItems.push({
            targetContract: action.evcAddress,
            onBehalfOfAccount: zeroAddress,
            value: 0n,
            data: enableCollateralData,
          })
        }

        // 2. Deposit (vault will use safeTransferFrom which tries Permit2 first)
        const depositData = encodeFunctionData({
          abi: eVaultImplementationAbi,
          functionName: 'deposit',
          args: [depositAmount, receiver],
        })

        batchItems.push({
          targetContract: action.vaultAddress,
          onBehalfOfAccount: account, // tokens pulled from main account
          value: 0n,
          data: depositData,
        })

        // Execute via EVC batch
        const configData = {
          abi: eulerVaultConnectorAbi,
          address: action.evcAddress,
          functionName: 'batch',
          chainId,
          args: [batchItems],
        } as const

        return ensureConfigTypes(configData)
      }

      // Fallback: Standard ERC20 deposit (requires pre-approved ERC20 allowance)
      const configData = {
        abi: eEarnVaultImplementationAbi,
        address: action.vaultAddress,
        functionName: 'deposit',
        chainId,
        args: [depositAmount, receiver],
      } as const

      return ensureConfigTypes(configData)
    },

    invalidates: () => getEulerPortfolioInvalidationKeys({ account, chainId }),
  }
}

function vaultNucleusDepositActionConfig(action: VaultDepositAction, context: ActionContext): ActionConfig {
  const { account, wagmiConfig, chainId } = context

  const depositAmount = toBigInt(action.token.toBaseUnit(action.value))

  if (!action.swapVaultContractAddress) {
    return {
      getWriteConfig: () => {
        const minimumMint = calculateMinimumMint(depositAmount, BigInt(action.rateInQuote), DEFAULT_SLIPPAGE)

        return ensureConfigTypes({
          abi: action.isTellerBouncerVault ? TELLER_BOUNCER_VAULT_ABI : TELLER_VAULT_ABI,
          address: action.tellerVaultAddress,
          functionName: 'deposit',
          chainId,
          args: action.isTellerBouncerVault
            ? [depositAmount, minimumMint]
            : [action.token.address, depositAmount, minimumMint],
        })
      },

      invalidates: () => getPortfolioBalanceInvalidationKeys({ account, chainId }),
    }
  }

  const configData = {
    abi: SWAP_VAULT_ABI,
    address: action.swapVaultContractAddress,
    functionName: 'deposit',
    chainId,
    value: action.token.address.toLowerCase() === zeroAddress.toLowerCase() ? depositAmount : undefined,
    args: [
      action.token.address,
      depositAmount,
      action.tokenOutAddress,
      action.tellerVaultAddress,
      0n,
      action.swapPath,
      false,
    ],
  } as const

  return {
    initialParamsQueryOptions: () => {
      return {
        ...queryOptions<bigint>({
          queryKey: [
            {
              entity: 'readContract',
              functionName: 'deposit',
              chainId,
              ...convertBigIntToString(configData.args),
            },
          ],
          queryFn: async () => {
            const result = await simulateContract(wagmiConfig, configData)

            return result.result
          },
        }),
        select: (data) => ({
          result: data,
          canBeSkipped: false,
        }),
      }
    },
    getWriteConfig: (initialParams: InitialParamsQueryResult) => {
      const minimumMint = calculateMinimumMint(
        initialParams.data?.result || 0n,
        BigInt(action.rateInQuote),
        DEFAULT_SLIPPAGE,
      )

      const configDataWithMinimumMint = {
        ...configData,
        args: [
          action.token.address,
          depositAmount,
          action.tokenOutAddress,
          action.tellerVaultAddress,
          minimumMint,
          action.swapPath,
          false,
        ],
      } as const

      return ensureConfigTypes(configDataWithMinimumMint)
    },

    invalidates: () => getPortfolioBalanceInvalidationKeys({ account, chainId }),
  }
}

// Helper function to convert BigInt values to strings in an array
function convertBigIntToString(args: readonly any[]): any[] {
  return args.map((arg) => (typeof arg === 'bigint' ? arg.toString() : arg))
}
